import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { plans, planText, type Plan } from "@/lib/plans";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/pages/home/components/Footer";
import Logo from "@/pages/home/components/Logo";
import CustomPlanCard from "./components/CustomPlanCard";
import PolicyLinks from "@/components/PolicyLinks";

export default function PricingPage() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const credits = profile?.credits ?? 0;

  // Contact and billing details are collected on the checkout page, which then
  // hands the customer over to DSK Bank's hosted payment page.
  const handleOrder = (tier: Plan) => {
    navigate(`/checkout?plan=${encodeURIComponent(tier.name)}`);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="border-b border-background-200/70 bg-background-50/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-16 px-4 md:px-6 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to="/generate"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("common.nav.generate")}
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("common.nav.pricing")}
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("pricing.nav.home")}
            </Link>
            <Link
              to="/profile"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("common.nav.profile")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            {t("pricing.eyebrow")}
          </span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950">
            {t("pricing.title")}
          </h1>
          <p className="mt-4 text-foreground-600">
            {t("pricing.subtitle")}
          </p>
          {user && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent-100 text-accent-900 px-4 py-2 text-sm font-medium">
              <i className="ri-flashlight-line"></i>
              {t("pricing.creditsRemaining", { count: credits })}
            </div>
          )}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {plans.map((tier) => {
            const text = planText(t, tier);
            return (
              <div
                key={tier.name}
                className={`relative flex flex-col rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${
                  tier.highlighted
                    ? "bg-gradient-to-b from-primary-600 to-accent-600 text-background-50 shadow-xl"
                    : "bg-background-50 border border-background-200/70 text-foreground-950"
                }`}
              >
                {tier.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-background-50 text-primary-600 text-xs font-semibold px-4 py-1 shadow-sm">
                    {t("pricing.mostPopular")}
                  </span>
                )}

                <h3 className="text-lg font-bold">{tier.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="text-4xl md:text-5xl font-bold tracking-tight">{text.price}</span>
                  <span
                    className={`text-sm mb-2 ${
                      tier.highlighted ? "text-background-50/80" : "text-foreground-500"
                    }`}
                  >
                    {text.period}
                  </span>
                </div>
                <p
                  className={`mt-2 text-sm ${
                    tier.highlighted ? "text-background-50/80" : "text-foreground-600"
                  }`}
                >
                  {text.tagline}
                </p>
                <div
                  className={`mt-2 inline-flex items-center gap-1.5 text-sm font-medium ${
                    tier.highlighted ? "text-accent-200" : "text-primary-600"
                  }`}
                >
                  <i className="ri-coins-line"></i>
                  {text.credits}
                </div>

                <ul className="mt-6 flex-1 space-y-3">
                  {text.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm">
                      <i
                        className={`ri-checkbox-circle-fill text-lg mt-0.5 ${
                          tier.highlighted ? "text-accent-200" : "text-primary-500"
                        }`}
                      ></i>
                      <span
                        className={
                          tier.highlighted ? "text-background-50/90" : "text-foreground-700"
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => handleOrder(tier)}
                  className={`mt-8 w-full whitespace-nowrap rounded-full py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02] cursor-pointer ${
                    tier.highlighted
                      ? "bg-background-50 text-primary-700"
                      : "bg-gradient-to-r from-primary-500 to-accent-500 text-background-50"
                  }`}
                >
                  {text.cta}
                </button>
              </div>
            );
          })}
        </div>

        <CustomPlanCard />

        <p className="mt-8 text-center text-xs text-foreground-500 flex items-center justify-center gap-2">
          <i className="ri-lock-line text-primary-600"></i>
          {t("pricing.secureCheckout")}
        </p>
        <p className="mt-2 text-center text-xs text-foreground-500">{t("common.prices.note")}</p>
        <PolicyLinks />
      </main>

      <Footer />
    </div>
  );
}